import { PluginListenerHandle } from "@capacitor/core";
import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { isCapacitorPlatform, isIosPlatform } from "@src/appV2/lib/utils";
import { ReferralRedemption } from "@src/appV2/Referral/RedeemCode/types";
import type { Worker } from "@src/appV2/Worker/api/types";
import type { Agent } from "@src/lib/interface";
import { BranchDeepLinks, BranchInitEvent } from "capacitor-branch-deep-links";
import { isEmpty } from "lodash";
import { Dispatch, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { readBranchDeepLink } from "./branchApi";
import { cbhCarePathMap } from "./cbh-care";
import { setAppUrlOpenHandler } from "./listener";
import { generateDynamicLinkListener } from "../dynamicLink/listener";

function isBranchLinkClickEvent(event: BranchInitEvent) {
  return (
    !isEmpty(event.referringParams) &&
    isDefined(event.referringParams["+clicked_branch_link"]) &&
    event.referringParams["+clicked_branch_link"]
  );
}

function isValidDeepLinkPath(path: unknown): boolean {
  if (!isDefined(path)) {
    return false;
  }
  try {
    new URL(path as string, environmentConfig.REACT_APP_WEB_APP_URL);
  } catch (error) {
    return false;
  }

  return true;
}

async function handleAppUrlOpen(
  originalUrl: string | undefined,
  deepLinkHandler: (params: { url: string }) => void
) {
  if (!isDefined(originalUrl) || isEmpty(originalUrl)) {
    return;
  }

  const url = new URL(originalUrl, environmentConfig.REACT_APP_WEB_APP_URL);

  const uriScheme = "health.clipboard.worker:";
  const uriLinkHost = environmentConfig.REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN;

  // URI Scheme with cbh.care: health.clipboard.worker://cbh.care/deep/link/path
  //   Used for push notification links
  if (url.protocol === uriScheme && url.hostname === uriLinkHost) {
    const deepLink = url.pathname + url.search + url.hash;

    deepLinkHandler({
      url: new URL(deepLink, environmentConfig.REACT_APP_WEB_APP_URL).toString(),
    });
    return;
  }

  // URI Scheme without cbh.care: health.clipboard.worker:///deep/link/path
  //   Used for Branch redirect when App Links don't open app on Android
  if (url.protocol === uriScheme) {
    const deepLink = url.hostname + url.pathname + url.search + url.hash;
    deepLinkHandler({
      url: new URL(deepLink, environmentConfig.REACT_APP_WEB_APP_URL).toString(),
    });
    return;
  }

  if (url.hostname === environmentConfig.REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN) {
    // Handle pre-existing Firebase Dynamic Links
    //  e.g. cbh.care/chat
    const cbhCareDestination = cbhCarePathMap[url.pathname];
    if (isDefined(cbhCareDestination)) {
      deepLinkHandler({
        url: new URL(cbhCareDestination, environmentConfig.REACT_APP_WEB_APP_URL).toString(),
      });
      return;
    }

    //  Handle cbh.care links with link query param
    //  e.g. cbh.care?link=https://hcp.clipboard.health/homes-v2/shift-discovery/map
    if (url.searchParams.has("link")) {
      deepLinkHandler({
        url: new URL(
          url.searchParams.get("link") ?? "",
          environmentConfig.REACT_APP_WEB_APP_URL
        ).toString(),
      });
      return;
    }

    logEvent(APP_V2_APP_EVENTS.UNSUPPORTED_CBH_CARE_DEEP_LINK, {
      url: originalUrl,
    });
    return;
  }

  // Branch Links
  if (url.origin.startsWith(environmentConfig.REACT_APP_BRANCH_DEEP_LINK_URL)) {
    if (isIosPlatform()) {
      // Branch links are handled by the Branch init listener on iOS
      return;
    }

    try {
      const { data } = await readBranchDeepLink({ url: originalUrl });
      const deeplinkPath = data["$deeplink_path"];
      if (deeplinkPath && isValidDeepLinkPath(deeplinkPath)) {
        deepLinkHandler({
          url: new URL(deeplinkPath, environmentConfig.REACT_APP_WEB_APP_URL).toString(),
        });
      } else {
        logEvent(APP_V2_APP_EVENTS.BRANCH_IO_INVALID_LINK, {
          metadata: {
            url: originalUrl,
            data,
          },
        });
      }
    } catch (error) {
      logError(APP_V2_APP_EVENTS.BRANCH_IO_READ_DEEP_LINK_FAILED, {
        error,
        metadata: {
          url: originalUrl,
        },
      });
    }
  }

  logEvent(APP_V2_APP_EVENTS.INVALID_DEEP_LINK, {
    metadata: {
      url: originalUrl,
    },
  });
}

export const useAppUrlListener = (
  worker: Agent | Worker | undefined,
  isAuthorized: boolean | undefined,
  setUrl: (url: string) => void,
  pushToLink: () => void,
  dispatch: Dispatch<any>,
  updateWorkerCache: (worker: Partial<Worker>) => void,
  setUnconsumedLink: (url: string) => void,
  setReferralRedemption: (referral: ReferralRedemption | undefined) => void
) => {
  const history = useHistory();

  useEffect(() => {
    let cancelled = false;

    const deepLinkHandler = generateDynamicLinkListener(
      worker,
      isAuthorized,
      setUrl,
      pushToLink,
      history,
      dispatch,
      updateWorkerCache,
      setUnconsumedLink,
      setReferralRedemption
    );

    setAppUrlOpenHandler((data) => handleAppUrlOpen(data.url, deepLinkHandler));

    let branchInitListener: PluginListenerHandle;

    const setupBranchInitListener = async () => {
      const listener = await BranchDeepLinks.addListener("init", async (event: BranchInitEvent) => {
        if (isBranchLinkClickEvent(event)) {
          const { referringParams } = event;

          const deepLinkPath = referringParams["$deeplink_path"];
          if (deepLinkPath && isValidDeepLinkPath(deepLinkPath)) {
            await deepLinkHandler({
              url: new URL(deepLinkPath, environmentConfig.REACT_APP_WEB_APP_URL).toString(),
            });
          }

          logEvent(APP_V2_APP_EVENTS.BRANCH_IO_INIT, {
            metadata: referringParams,
          });
        }
      });
      if (cancelled) {
        listener.remove();
      } else {
        branchInitListener = listener;
      }
    };

    if (isCapacitorPlatform() && isIosPlatform()) {
      setupBranchInitListener();
    }

    return () => {
      cancelled = true;
      branchInitListener?.remove();
    };
  });
};
